import { useEffect, useState } from "react";

/**
 * True once the browser has STYLED a commit that carried `ready` for this `key` with the thing still
 * unarmed — two animation frames after `ready` (re)turns true, never in the commit itself.
 *
 * For a CSS transition that must not run on the commit that puts things in place: a transition is
 * defined by the AFTER-change style, so a style-change event that both moves an element and arms its
 * `transition` animates it from where it was. پاسور's pool (Task 4 review Minor 7): the room is
 * measured in a layout effect and the real slot positions commit before paint — armed with them,
 * sixteen cards slide from the fallback room to the real one under the fly-in.
 *
 * ⚠️ ONE COMMIT LATER IS NOT ENOUGH, AND «AFTER PAINT» WAS NOT WHAT IT GOT (Task 4 re-review 4b, P1
 * Task 5 note 14). The first fix armed in a PASSIVE effect, trusting it to run after paint; but the
 * layout effect's `setRoom` is a SyncLane update, and react-dom 18.3.1 flushes a SyncLane commit's
 * passive effects SYNCHRONOUSLY at its end — so the arming commit followed the measured one inside the
 * same task, nothing styled the measured positions in between, and the browser still compared the
 * fallback positions with the armed, measured ones: the slide ran. Arming from animation frames puts
 * a real style recalc in between: the FIRST frame styles the measured positions with the transition
 * off (now they are the before-change style); a ResizeObserver re-measure that lands in that frame (it
 * runs after layout, before paint) commits before the SECOND frame, which arms with nothing left to
 * move. The browser half is the layout kit's `probe-slot-slide.cjs` (no CSSTransition on a slot after
 * mount at 384 / 390, red on f47a42f); `use-armed-after.test.tsx` pins the timing.
 *
 * A new `key` disarms IN THE SAME RENDER (a new deal's pool mounts and is measured with the
 * transition off, then re-arms two frames later). A runtime without `requestAnimationFrame` arms on
 * the effect, as before.
 */
export function useArmedAfter(ready: boolean, key: string | number = 0): boolean {
  const [armedKey, setArmedKey] = useState<string | number | null>(null);
  useEffect(() => {
    if (!ready || armedKey === key) return undefined;
    if (typeof window.requestAnimationFrame !== "function") {
      setArmedKey(key);
      return undefined;
    }
    let id = window.requestAnimationFrame(() => {
      id = window.requestAnimationFrame(() => setArmedKey(key));
    });
    return () => window.cancelAnimationFrame(id);
  }, [ready, key, armedKey]);
  return ready && armedKey === key;
}
